import { memo, useCallback, useRef, useState } from "react";
import AnchoredPanel from "../../../../GlobalComponents/AnchoredPanel/AnchoredPanel";

/**
 * The unit each formula variable is converted to before the formula runs —
 * `variable_units_json`, `{ Q594: unitId }`. A variable without an entry is
 * used as entered.
 *
 * @param {object} props
 * @param {string[]} props.variables
 * @param {Record<string, number|string>} props.value
 * @param {{ id: number, unit: string, unit_name: string }[]} props.unitMaster
 * @param {boolean} [props.dirty]
 * @param {(value: Record<string, number>) => void} props.onChange
 */
function VariableUnitsCell({ variables, value, unitMaster, dirty, onChange }) {
  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const cellRef = useRef(null);

  if (!variables.length) return <span className="kgb-readonly kgb-muted">No variables</span>;

  const labelFor = (unitId) => unitMaster.find((unit) => String(unit.id) === String(unitId))?.unit ?? `unit ${unitId}`;
  const converted = variables.filter((variable) => value[variable] != null && value[variable] !== "");

  const setUnit = (variable, unitId) => {
    const next = Object.fromEntries(Object.entries(value).filter(([key]) => key !== variable));
    if (unitId) next[variable] = Number(unitId);
    onChange(next);
  };

  return (
    <div className={`kgb-chipcell${dirty ? " kgb-chipcell--dirty" : ""}`} ref={cellRef}>
      {converted.map((variable) => (
        <span key={variable} className="kgb-chip kgb-chip--unit">
          <span>{variable}</span>
          <span className="kgb-chip-key">→ {labelFor(value[variable])}</span>
        </span>
      ))}
      <button type="button" className="kgb-add-link" aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        {converted.length ? "Edit" : "+ Convert"}
      </button>

      {open ? (
        <AnchoredPanel anchorRef={cellRef} onClose={close} className="kgb-picker kgb-picker--wide">
          <p className="kgb-picker-title">Convert each variable to</p>
          {variables.map((variable) => (
            <div className="kgb-editor-row" key={variable}>
              <span className="kgb-editor-unit">{variable}</span>
              <select
                className="kgb-editor-key"
                value={value[variable] ?? ""}
                aria-label={`Unit ${variable} is converted to`}
                onChange={(e) => setUnit(variable, e.target.value)}
              >
                <option value="">No conversion</option>
                {unitMaster.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.unit_name} ({unit.unit})
                  </option>
                ))}
              </select>
            </div>
          ))}
        </AnchoredPanel>
      ) : null}
    </div>
  );
}

export default memo(VariableUnitsCell);
